import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../api/axios";
import Pagination from "../../components/Pagination";
import { toast } from "react-toastify";

const statusOptions = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
];

const planOptions = [
  { value: "", label: "All Plans" },
  { value: "1", label: "Monthly" },
  { value: "2", label: "3 Months" },
  { value: "3", label: "6 Months" },
  { value: "4", label: "1 Year" },
];

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";

const SignalXList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 15,
    startDate: "",
    endDate: "",
    status: "",
    username: "",
    plan: "",
  });
  const [tempFilters, setTempFilters] = useState({
    page: 1,
    pageSize: 15,
    startDate: "",
    endDate: "",
    status: "",
    username: "",
    plan: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRows: 0,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.page) params.page = filters.page;
      if (filters.pageSize) params.pageSize = filters.pageSize;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.status) params.status = filters.status;
      if (filters.username) params.username = filters.username;
      if (filters.plan) params.plan = filters.plan;

      const res = await axiosInstance.get(
        "/api/admin/services/signalx-list",
        { params }
      );

      if (res.data.success) {
        setData(res.data.data.data || []);
        setPagination({
          currentPage: parseInt(res.data.data.currentPage) || 1,
          totalPages: res.data.data.totalPages || 1,
          totalRows: res.data.data.totalRows || 0,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch Signal X list");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTempChange = (key, value) => {
    setTempFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const applyFilters = () => {
    setFilters({ ...tempFilters, page: 1 });
  };

  const resetFilters = () => {
    const reset = {
      page: 1,
      pageSize: 15,
      startDate: "",
      endDate: "",
      status: "",
      username: "",
      plan: "",
    };
    setTempFilters(reset);
    setFilters(reset);
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }));
    setTempFilters((prev) => ({ ...prev, page }));
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : "-");

  const statusBadge = (status) => {
    const cls =
      status === "active"
        ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
        : status === "expired"
        ? "bg-red-500/15 text-red-400 border border-red-500/20"
        : "bg-white/[0.05] text-muted-foreground border border-border/50";
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
        {status}
      </span>
    );
  };

  const thCls = "border-b border-border/70 px-5 py-3 text-[10px] font-medium uppercase tracking-[1.5px] text-[#9aa1ac] text-left";
  const tdCls = "border-b border-border/40 px-5 py-3.5 text-sm text-foreground";

  return (
    <div className="p-4">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Services
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        Signal X — <span className="text-gradient-ginox">List</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        Browse and filter Signal X subscription records.
      </p>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Username / WalletAddress
            </label>
            <input
              type="text"
              value={tempFilters.username}
              onChange={(e) => handleTempChange("username", e.target.value)}
              placeholder="Search…"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Status
            </label>
            <select
              value={tempFilters.status}
              onChange={(e) => handleTempChange("status", e.target.value)}
              className={inputCls}
            >
              {statusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Plan
            </label>
            <select
              value={tempFilters.plan}
              onChange={(e) => handleTempChange("plan", e.target.value)}
              className={inputCls}
            >
              {planOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Start Date
            </label>
            <input
              type="date"
              value={tempFilters.startDate}
              onChange={(e) => handleTempChange("startDate", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              End Date
            </label>
            <input
              type="date"
              value={tempFilters.endDate}
              onChange={(e) => handleTempChange("endDate", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page Size
            </label>
            <select
              value={tempFilters.pageSize}
              onChange={(e) =>
                handleTempChange("pageSize", parseInt(e.target.value))
              }
              className={inputCls}
            >
              {[15, 25, 50, 100].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={applyFilters}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            Apply Filters
          </button>
          <button
            onClick={resetFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={thCls}>#</th>
              <th className={thCls}>Username</th>
              <th className={thCls}>Tranx ID</th>
              <th className={thCls}>Plan</th>
              <th className={thCls}>Amount</th>
              <th className={thCls}>Wallet</th>
              <th className={thCls}>Status</th>
              <th className={thCls}>Created</th>
              <th className={thCls}>Expires</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="py-12 text-center text-sm text-muted-foreground">
                  Loading...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-12 text-center text-sm text-muted-foreground">
                  No records found
                </td>
              </tr>
            ) : (
              data.map((row, idx) => (
                <tr key={row._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className={`${tdCls} font-mono`}>
                    {(pagination.currentPage - 1) * filters.pageSize + idx + 1}
                  </td>
                  <td className={tdCls}>{row.username}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{row.tranx_id}</td>
                  <td className={tdCls}>{row.planName}</td>
                  <td className={`${tdCls} font-mono`}>{row.amount}</td>
                  <td className={tdCls}>
                    {row.wallet_type}
                    {row.isFree && (
                      <span className="ml-1.5 inline-flex px-1.5 py-0.5 text-xs rounded bg-yellow-500/15 text-yellow-400 border border-yellow-500/20">
                        Free
                      </span>
                    )}
                  </td>
                  <td className={tdCls}>{statusBadge(row.status)}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{formatDate(row.createdAt)}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{formatDate(row.expiresAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default SignalXList;
